import React, { useState, useEffect } from 'react';
import { DatePickerComponent, ChangeEventArgs } from '@syncfusion/ej2-react-calendars';
import { useFormContext } from 'react-hook-form';
import { TooltipComponent } from '@syncfusion/ej2-react-popups';
import { Modal } from 'react-bootstrap';
import { get } from 'lodash';

interface IInputProps {
  name: string;
  id: string;
  label?: string;
  placeholder?: string;
  getDayWeek?: (date: Date) => void;
  minDate?: Date;
  maxDate?: Date;
  req?: string;
  disableDate?: boolean;
  modal?: string;
  xl?: number;
  md?: number;
  col?: number;

  defaultValue?: Date | null | '';
}


const isValidDate = (value: unknown): value is Date =>
  value instanceof Date && !isNaN(value.getTime());

const getDateValue = (value: unknown): Date | null => {
  if (value === undefined || value === null || value === '') return null;

  if (isValidDate(value)) return value;

  if (typeof value === 'string') {
    const normalizedValue = value.split('T')[0];
    const isoDateMatch = /^(\d{4})-(\d{2})-(\d{2})$/.exec(normalizedValue);

    if (isoDateMatch) {
      return new Date(
        Number(isoDateMatch[1]),
        Number(isoDateMatch[2]) - 1,
        Number(isoDateMatch[3])
      );
    }
  }

  const parsedDate = new Date(value as string | number | Date);
  return isValidDate(parsedDate) ? parsedDate : null;
};

const getComparableDate = (value: unknown): Date | null => {
  const date = getDateValue(value);
  if (!date) return null;

  const comparableDate = new Date(date);
  comparableDate.setHours(0, 0, 0, 0);
  return comparableDate;
};

const isToday = (value: Date) => {
  const today = new Date();
  return getComparableDate(value)?.getTime() === getComparableDate(today)?.getTime();
};

const validateDateRange = (value: unknown, minDate?: Date, maxDate?: Date) => {
  if (value === undefined || value === null || value === '') return true;

  const selectedDate = getComparableDate(value);
  if (!selectedDate) return 'Introduce una fecha válida';

  const minimumDate = minDate ? getComparableDate(minDate) : null;
  if (minimumDate && selectedDate < minimumDate) {
    return `La fecha no puede ser anterior a ${minimumDate.toLocaleDateString('es-ES')}`;
  }

  const maximumDate = maxDate ? getComparableDate(maxDate) : null;
  if (maximumDate && selectedDate > maximumDate) {
    return isToday(maxDate)
      ? 'La fecha no puede ser futura'
      : `La fecha no puede ser posterior a ${maximumDate.toLocaleDateString('es-ES')}`;
  }

  return true;
};

const FormInputFecha: React.FC<IInputProps> = ({
  name,
  id,
  label,
  getDayWeek,
  minDate,
  maxDate,
  req,
  disableDate,
  modal,
  xl = 12,
  md = 12,
  col = 12,
  defaultValue,
  placeholder,
}) => {
  const {
    register,
    formState: { errors },
    setValue,
  } = useFormContext();

  const initialDate = isValidDate(defaultValue) ? defaultValue : null;
  const [selectedDate, setSelectedDate] = useState<Date | null>(initialDate);

  useEffect(() => {
    if (isValidDate(defaultValue) && !selectedDate) {
      setSelectedDate(defaultValue);
    }
  }, [defaultValue, selectedDate]);

  const handleDateChange = (args: ChangeEventArgs) => {
    const date = args.value as Date | null;
    if (date) {
      date.setHours(12, 0, 0, 0);
      setSelectedDate(date);
      setValue(name, date, { shouldValidate: true, shouldDirty: true });
      if (getDayWeek) {
        getDayWeek(date);
      }
    } else {
      setSelectedDate(null);
      setValue(name, null, { shouldValidate: true, shouldDirty: true });
    }
  };

  const errorMessage = get(errors, name)?.message as string | undefined;

  /////// MODAL ///////
  const [show, setShow] = useState(false);
  const handleShow = () => setShow(true);
  const handleClose = () => setShow(false);

  return (
    <div className={`col-xl-${xl} col-md-${md} col-${col}`}>
      <div className={`input-block ${id} ${errorMessage ? 'error' : ''}`}>
        {(label || modal) && (
          <div className="d-flex align-items-center gap-1 mb-1">
            {label && (
              <label htmlFor={id} className="input-block__label mb-0">
                {label}
              </label>
            )}
            {modal && (
              <TooltipComponent content="Más información">
                <div onClick={handleShow} className="information-icon-orange p-0" />
              </TooltipComponent>
            )}
          </div>
        )}

        <DatePickerComponent
          format="dd-MM-yyyy"
          name={name}
          id={id}
          change={handleDateChange}
          locale=''
          value={selectedDate ?? undefined}
          firstDayOfWeek={1}
          {...register(name, {
            required: req,
            validate: (value) => validateDateRange(value, minDate, maxDate),
          })}
          min={minDate}
          max={maxDate}
          disabled={disableDate}
          placeholder={placeholder}
          allowEdit={false}
          openOnFocus={true}
        />

        {errorMessage && <p className="error m-0">{errorMessage}</p>}

        <Modal show={show} onHide={handleClose}>
          <Modal.Header closeButton>
            <Modal.Title>{label}</Modal.Title>
          </Modal.Header>
          <Modal.Body>{modal}</Modal.Body>
        </Modal>
      </div>
    </div>
  );
};

export default FormInputFecha;

